import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  FaMoneyCheckAlt, 
  FaCalendarAlt, 
  FaUser, 
  FaHome, 
  FaFileInvoice,
  FaCreditCard,
  FaCheckCircle,
  FaExclamationTriangle
} from 'react-icons/fa';
import styles from './RegisterPayment.module.css';

const RegisterPayment = ({ onClose }) => {
  const dispatch = useDispatch();
  const { 
    activeContracts,
    loading,
    error 
  } = useSelector(state => state.rental);

  const [formData, setFormData] = useState({
    contractId: '',
    amount: '',
    currency: 'ARS',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'transferencia',
    concept: 'alquiler',
    period: '',
    periodStart: '',
    periodEnd: '',
    reference: '',
    notes: ''
  });

  const [selectedContract, setSelectedContract] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const paymentMethods = [
    { value: 'transferencia', label: 'Transferencia Bancaria' },
    { value: 'efectivo', label: 'Efectivo' },
    { value: 'cheque', label: 'Cheque' },
    { value: 'debito', label: 'Débito Automático' },
    { value: 'mercadopago', label: 'Mercado Pago' },
    { value: 'otro', label: 'Otro' }
  ];

  const paymentConcepts = [
    { value: 'alquiler', label: 'Alquiler Mensual' },
    { value: 'deposito', label: 'Depósito en Garantía' },
    { value: 'expensas', label: 'Expensas' },
    { value: 'servicios', label: 'Servicios' },
    { value: 'comision', label: 'Comisión' },
    { value: 'multa', label: 'Multa/Penalidad' },
    { value: 'otro', label: 'Otro Concepto' }
  ];

  useEffect(() => {
    // Cargar contratos activos si no están disponibles
    if (!activeContracts || activeContracts.length === 0) {
      dispatch({ type: 'FETCH_ACTIVE_CONTRACTS_REQUEST' });
    }

    // Si hay solo un contrato, seleccionarlo automáticamente
    if (activeContracts && activeContracts.length === 1) {
      handleContractSelect(activeContracts[0].id);
    }
  }, [dispatch, activeContracts]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Limpiar error de validación al cambiar el campo
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleContractSelect = (contractId) => {
    const contract = activeContracts.find(c => c.id === contractId);
    setSelectedContract(contract);
    
    setFormData(prev => ({
      ...prev,
      contractId,
      amount: contract?.monthlyRent || '',
      currency: contract?.currency || 'ARS',
      period: generateCurrentPeriod()
    }));
  };

  const generateCurrentPeriod = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${month}/${year}`;
  };

  const calculatePeriodDates = (period) => {
    if (!period.includes('/')) return { start: '', end: '' };
    
    const [month, year] = period.split('/');
    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 0);
    
    return {
      start: startDate.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0]
    };
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.contractId) {
      errors.contractId = 'Debe seleccionar un contrato';
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      errors.amount = 'Debe ingresar un monto válido';
    }

    if (!formData.paymentDate) {
      errors.paymentDate = 'Debe seleccionar una fecha de pago';
    }

    if (!formData.period) {
      errors.period = 'Debe especificar el período';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const periodDates = calculatePeriodDates(formData.period);
      
      const paymentData = {
        ...formData,
        amount: parseFloat(formData.amount),
        periodStart: periodDates.start,
        periodEnd: periodDates.end,
        contractInfo: {
          propertyAddress: selectedContract.property.address,
          tenantName: selectedContract.tenant.name,
          ownerName: selectedContract.owner.name
        }
      };

      await dispatch({
        type: 'REGISTER_PAYMENT_REQUEST',
        payload: paymentData
      });

      // Mostrar mensaje de éxito y cerrar modal
      alert('Pago registrado exitosamente');
      onClose();

    } catch (error) {
      console.error('Error registrando pago:', error);
      alert('Error al registrar el pago. Intente nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (amount, currency) => {
    if (!amount) return '';
    const formatter = new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: currency || 'ARS'
    });
    return formatter.format(amount);
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>
          <div className={styles.spinner}></div>
          <p>Cargando contratos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <FaMoneyCheckAlt className={styles.titleIcon} />
          <h2 className={styles.title}>Registrar Pago</h2>
        </div>
        <button 
          onClick={onClose}
          className={styles.closeButton}
          type="button"
        >
          ×
        </button>
      </div>

      {error && (
        <div className={styles.errorBanner}>
          <FaExclamationTriangle />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className={styles.form}>
        
        {/* Selección de Contrato */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            <FaFileInvoice />
            Contrato de Alquiler
          </h3>
          
          <div className={styles.formGroup}>
            <label htmlFor="contractId">Seleccionar Contrato *</label>
            <select
              id="contractId"
              name="contractId"
              value={formData.contractId}
              onChange={(e) => handleContractSelect(e.target.value)}
              className={`${styles.select} ${validationErrors.contractId ? styles.error : ''}`}
              required
            >
              <option value="">-- Seleccione un contrato --</option>
              {activeContracts?.map(contract => (
                <option key={contract.id} value={contract.id}>
                  {contract.property.address} - {contract.tenant.name}
                </option>
              ))}
            </select>
            {validationErrors.contractId && (
              <span className={styles.errorText}>{validationErrors.contractId}</span>
            )}
          </div>

          {selectedContract && (
            <div className={styles.contractInfo}>
              <div className={styles.contractCard}>
                <div className={styles.contractDetail}>
                  <FaHome />
                  <div>
                    <span className={styles.label}>Propiedad:</span>
                    <span>{selectedContract.property.address}</span>
                  </div>
                </div>
                <div className={styles.contractDetail}>
                  <FaUser />
                  <div>
                    <span className={styles.label}>Inquilino:</span>
                    <span>{selectedContract.tenant.name}</span>
                  </div>
                </div>
                <div className={styles.contractDetail}>
                  <FaMoneyCheckAlt />
                  <div>
                    <span className={styles.label}>Alquiler Mensual:</span>
                    <span>{formatCurrency(selectedContract.monthlyRent, selectedContract.currency)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Información del Pago */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            <FaCreditCard />
            Información del Pago
          </h3>
          
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label htmlFor="amount">Monto del Pago *</label>
              <div className={styles.inputWithCurrency}>
                <select
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                  className={styles.currencySelect}
                >
                  <option value="ARS">ARS</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
                <input
                  type="number"
                  id="amount"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  className={`${styles.input} ${validationErrors.amount ? styles.error : ''}`}
                  required
                />
              </div>
              {validationErrors.amount && (
                <span className={styles.errorText}>{validationErrors.amount}</span>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="paymentDate">Fecha de Pago *</label>
              <input
                type="date"
                id="paymentDate"
                name="paymentDate"
                value={formData.paymentDate}
                onChange={handleChange}
                className={`${styles.input} ${validationErrors.paymentDate ? styles.error : ''}`}
                required
              />
              {validationErrors.paymentDate && (
                <span className={styles.errorText}>{validationErrors.paymentDate}</span>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="paymentMethod">Método de Pago</label>
              <select
                id="paymentMethod"
                name="paymentMethod"
                value={formData.paymentMethod}
                onChange={handleChange}
                className={styles.select}
              >
                {paymentMethods.map(method => (
                  <option key={method.value} value={method.value}>
                    {method.label}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="concept">Concepto del Pago</label>
              <select
                id="concept"
                name="concept"
                value={formData.concept}
                onChange={handleChange}
                className={styles.select}
              >
                {paymentConcepts.map(concept => (
                  <option key={concept.value} value={concept.value}>
                    {concept.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Período y Referencias */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            <FaCalendarAlt />
            Período y Referencias
          </h3>
          
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label htmlFor="period">Período (MM/YYYY) *</label>
              <input
                type="text"
                id="period"
                name="period"
                value={formData.period}
                onChange={handleChange}
                placeholder="01/2024"
                pattern="[0-9]{2}/[0-9]{4}"
                className={`${styles.input} ${validationErrors.period ? styles.error : ''}`}
                required
              />
              <span className={styles.helpText}>
                Formato: MM/YYYY (ej: 01/2024)
              </span>
              {validationErrors.period && (
                <span className={styles.errorText}>{validationErrors.period}</span>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="reference">Número de Referencia</label>
              <input
                type="text"
                id="reference"
                name="reference"
                value={formData.reference}
                onChange={handleChange}
                placeholder="N° de transferencia, cheque, etc."
                className={styles.input}
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="notes">Observaciones</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Notas adicionales sobre el pago..."
              className={styles.textarea}
              rows="3"
            />
          </div>
        </div>

        {/* Acciones */}
        <div className={styles.formActions}>
          <button
            type="button"
            onClick={onClose}
            className={`${styles.button} ${styles.secondary}`}
            disabled={isSubmitting}
          >
            Cancelar
          </button>
          
          <button
            type="submit"
            className={`${styles.button} ${styles.primary}`}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <div className={styles.buttonSpinner}></div>
                Registrando...
              </>
            ) : (
              <>
                <FaCheckCircle />
                Registrar Pago
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RegisterPayment;